import {
  createSelector, createSlice, PayloadAction,
} from '@reduxjs/toolkit';
import moment, { Moment, MomentInput } from 'moment-timezone';
import { AVAILABLE_LOCALES } from 'appenv';
import { AvailableLocale } from 'i18n/types';

export interface LocalizationState {
  useLocalTime: boolean;
  eventTimezone: string;
  locale: AvailableLocale;
  availableLocales: AvailableLocale[];
}

type MomentLocalizer = (time: MomentInput) => Moment;

// eslint-disable-next-line import/no-mutable-exports
export let availableLocales: AvailableLocale[] = AVAILABLE_LOCALES;

/**
 * @description navigator.languages returns an array of user's preferred languages.
 * The language is described using BCP 47 language tags.
 * @returns AvailableLocale | undefined
 */
const detectLocale = () => {
  const detected = [
    localStorage.getItem('locale'),
    ...(navigator.languages || []),
    ...[(navigator as any)?.userLanguage].filter((it) => it),
    availableLocales[0],
  ].filter((it) => !!it)
    .map((it) => {
      const lang = it.toLowerCase();
      if (lang.startsWith('en')) {
        return 'en';
      }
      if (lang === 'zh-tw' || lang === 'zh-hk') {
        return 'zh-Hant';
      }
      if (lang === 'zh-cn' || lang === 'zh-sg') {
        return 'zh-Hans';
      }
      return it;
    });
  return detected.find((it) => availableLocales.includes(it));
};

export const initialState: LocalizationState = {
  useLocalTime: true,
  eventTimezone: moment.tz.guess(),
  locale: null,
  availableLocales,
};

export const selectLocalizationState = (state: any) => state.localization as LocalizationState;
export const selectMomentLocalizer: ((state: any) => MomentLocalizer) = createSelector(
  [selectLocalizationState],
  (state: LocalizationState) => {
    const { eventTimezone } = state;
    return state.useLocalTime
      ? (time) => moment(time).local()
      : (time) => moment(time).tz(eventTimezone);
  },
);

export const selectLocale = (state: any): AvailableLocale => state.localization.locale;

// rfc4646 => rfc5646
export const selectMomentLocaleKey = (state: any): any => {
  const locale = state?.localization?.locale?.toLocaleLowerCase();

  if (locale === 'zh-hans') {
    return 'zh-cn';
  }
  if (locale === 'zh-hant') {
    return 'zh-tw';
  }
  return locale ?? 'en';
};

export const selectAvailableLocales = (state: any): AvailableLocale[] => state.localization.availableLocales;

export const localizationSlice = createSlice({
  name: 'localization',
  initialState,
  reducers: {
    setUseLocalTime: (state, action: PayloadAction<boolean>) => {
      state.useLocalTime = action.payload;
    },
    setEventTimeZone: (state, action: PayloadAction<string>) => {
      state.eventTimezone = action.payload;
    },
    setLocale: (state, action: PayloadAction<AvailableLocale>) => {
      localStorage.setItem('locale', action.payload);
      state.locale = action.payload;
    },
    setAvailableLocales: (state, action: PayloadAction<AvailableLocale[]>) => {
      state.availableLocales = action.payload;
      availableLocales = state.availableLocales;
    },
    setLocaleByDetection: (state) => {
      const detectedLocale = detectLocale();
      const matched = state.availableLocales.find((it) => detectedLocale === it);
      if (matched) {
        state.locale = matched;
      } else {
        [state.locale] = state.availableLocales;
      }
    },
  },
});

export default localizationSlice.reducer;
